import { useCallback } from "react";

import { useHistoryState } from "utilities/historyState/useHistoryState";
import {
    restoreScrollPosition,
    useScrollPositionHistory,
} from "utilities/historyState/useScrollPositionHistory";
import { lazyLoadComponent } from "utilities/LazyLoad.utility";

import { type IBTPaginationHistoryState } from "commonComponents/btWrappers/BTPagination/BTPagination";
import { BTSuspense } from "commonComponents/btWrappers/BTSuspense/BTSuspense";
import { type IBTGridProps } from "commonComponents/utilities/Grid/BTGrid";
import {
    type GridColumn,
    type IScrollRestorationConfig,
} from "commonComponents/utilities/Grid/GridContainer.types";

import { GridCardView, type IGridCardViewConfig } from "./GridCardView/GridCardView";
import { useShouldShowGridCardView } from "./GridCardView/useShouldShowGridCardView";

const BTGrid = lazyLoadComponent(() => import("./BTGrid"));

interface IGridContainerViewProps extends IBTGridProps {
    gridCardView?: IGridCardViewConfig;
    /**
     * Every available column for this grid (including ones the user has
     * hidden in their saved view). The card view filters this to the
     * `IsAlwaysIncluded` subset rather than reading from the user-visible
     * `columnsList` so cards retain a stable, server-defined shape.
     */
    columnsListAll?: GridColumn<unknown>[];
    /**
     * Opt in to automatic window-scroll save/restore across navigation for this list
     * (desktop grid and card view). Presence of the config enables it; pass it only on
     * full-page, window-scrolling list grids. Omit on embedded/nested grids.
     */
    scrollRestorationConfig?: IScrollRestorationConfig;
}

/**
 * Decides whether to render the Kendo grid (`<BTGrid>`) or the mobile-friendly
 * card view based on the `gridCardView` prop and the current viewport/webview.
 *
 * If grouping is active, falls back to the grid even in card-view mode — the
 * grouped data shape and group-header rendering aren't supported in card view
 * yet.
 */
export const GridContainerView: React.FC<IGridContainerViewProps> = (props) => {
    const {
        gridCardView,
        groups,
        columnsList,
        columnsListAll,
        values,
        sort,
        onSortChange,
        isSortDisabled,
        hasSelectableRows,
        singleSelect,
        onSelectChange,
        onHeaderSelectChange,
        setFieldValue,
        scrollRestorationConfig,
        ...btGridProps
    } = props;

    const shouldShowCardView = useShouldShowGridCardView(gridCardView?.enabled);
    const hasActiveGrouping = groups != null && groups.length > 0;

    // Centralized window-scroll save/restore for list grids, opt-in via
    // `scrollRestorationConfig` so embedded/nested grids stay unaffected. The save hook
    // only binds its scroll-end handler when enabled; the restore runs once the grid
    // (or card view) signals its initial render is complete.
    const scrollRestorationEnabled =
        scrollRestorationConfig != null && (scrollRestorationConfig.enabled ?? true);
    const scrollRestorationJobIds = scrollRestorationConfig?.jobIds;
    const historyState = useHistoryState<IBTPaginationHistoryState>({});
    useScrollPositionHistory(scrollRestorationJobIds, { enabled: scrollRestorationEnabled });

    const consumerOnInitialRenderComplete = btGridProps.onInitialRenderComplete;
    const handleInitialRenderComplete = useCallback(() => {
        if (scrollRestorationEnabled) {
            restoreScrollPosition(
                historyState?.scrollPosition,
                historyState?.jobIds,
                scrollRestorationJobIds,
            );
        }
        consumerOnInitialRenderComplete?.();
    }, [
        scrollRestorationEnabled,
        historyState,
        scrollRestorationJobIds,
        consumerOnInitialRenderComplete,
    ]);

    if (shouldShowCardView && !hasActiveGrouping && gridCardView && columnsListAll) {
        return (
            <GridCardView
                columnsListAll={columnsListAll}
                rowData={values.rowData}
                sortingData={sort}
                onSortChange={onSortChange}
                config={gridCardView}
                isSortDisabled={isSortDisabled}
                hasSelectableRows={hasSelectableRows}
                singleSelect={singleSelect}
                onSelectChange={onSelectChange}
                onHeaderSelectChange={onHeaderSelectChange}
                setFieldValue={setFieldValue}
                // Same render-complete signal the grid uses, composed with central
                // scroll restoration so card-view lists restore scroll position too.
                onInitialRenderComplete={handleInitialRenderComplete}
            />
        );
    }

    return (
        <BTSuspense>
            <div style={{ minWidth: 0 }}>
                <BTGrid
                    {...btGridProps}
                    columnsList={columnsList}
                    values={values}
                    sort={sort}
                    onSortChange={onSortChange}
                    isSortDisabled={isSortDisabled}
                    hasSelectableRows={hasSelectableRows}
                    singleSelect={singleSelect}
                    onSelectChange={onSelectChange}
                    onHeaderSelectChange={onHeaderSelectChange}
                    setFieldValue={setFieldValue}
                    groups={groups}
                    // Override any spread onInitialRenderComplete with the composed
                    // handler so the desktop grid also runs central scroll restoration.
                    onInitialRenderComplete={handleInitialRenderComplete}
                />
            </div>
        </BTSuspense>
    );
};
